import { useState } from "react";
import { motion } from "framer-motion";
import {
  Lightbulb,
  Magnet,
  FileText,
  PlayCircle,
  Play,
  TrendingUp,
  Plus,
  Check,
  ArrowRight,
  Heart,
  CheckCircle,
  MoreVertical,
} from "lucide-react";
import { cn } from "@/lib/utils";

type Step = {
  id: number;
  Icon: typeof Lightbulb;
  iconTint: string;
  iconStroke: string;
  bg: string;
  titleBold: string;
  rest: string;
};

const STEPS: readonly Step[] = [
  {
    id: 1,
    Icon: Lightbulb,
    iconTint: "#FFEFE2",
    iconStroke: "#E8630A",
    bg: "#FBE9D7",
    titleBold: "Nie wiesz co nagrać?",
    rest: "Masz blokadę, my mamy świeże pomysły.",
  },
  {
    id: 2,
    Icon: Magnet,
    iconTint: "#E8F4FD",
    iconStroke: "#1A7ACC",
    bg: "#E0EFFA",
    titleBold: "Magnes łapie pomysły",
    rest: "codziennie świeże inspiracje dopasowane do Twojej tematyki.",
  },
  {
    id: 3,
    Icon: FileText,
    iconTint: "#E6F3EB",
    iconStroke: "#2D8B4E",
    bg: "#E0EEDF",
    titleBold: "Gotowy scenariusz",
    rest: "pełny skrypt, 10 otwarć filmu i sprawdzone fakty w mniej niż minutę.",
  },
  {
    id: 4,
    Icon: PlayCircle,
    iconTint: "#F1E6F8",
    iconStroke: "#7C4FAB",
    bg: "#E8DDF2",
    titleBold: "Nagrywaj z teleprompterem",
    rest: "czytasz prosto z ekranu telefonu, bez stresu i bez błędów.",
  },
  {
    id: 5,
    Icon: TrendingUp,
    iconTint: "#FFEFE2",
    iconStroke: "#E8630A",
    bg: "#F8D9B8",
    titleBold: "Publikuj i rośnij",
    rest: "lepsze treści to więcej wyświetleń, zapisów i obserwujących.",
  },
] as const;

const Squiggle = ({ w = 200, color = "#E8630A", stroke = 4 }) => (
  <svg
    width={w}
    height="14"
    viewBox={`0 0 ${w} 14`}
    className="block"
    aria-hidden="true"
  >
    <path
      d={`M2 9 Q ${w * 0.2} 2, ${w * 0.4} 7 T ${w * 0.78} 8 T ${w - 2} 6`}
      stroke={color}
      strokeWidth={stroke}
      strokeLinecap="round"
      fill="none"
    />
  </svg>
);

const Mascot = ({ size = 180, label = "Viralisek" }: { size?: number; label?: string }) => (
  <div
    style={{ width: size, height: size }}
    className="flex items-center justify-center rounded-3xl"
    aria-label={label}
  >
    <span style={{ fontSize: size * 0.65, lineHeight: 1 }} role="img" aria-hidden="true">
      🦊
    </span>
  </div>
);

const Phone = ({
  w = 260,
  h = 540,
  bg = "#FFFFFF",
  notch = true,
  children,
}: {
  w?: number;
  h?: number;
  bg?: string;
  notch?: boolean;
  children: React.ReactNode;
}) => (
  <div
    style={{
      width: w,
      height: h,
      background: "#1A1A1A",
      boxShadow:
        "0 24px 48px -20px rgba(26,26,26,0.4), 0 6px 14px -6px rgba(26,26,26,0.2)",
    }}
    className="rounded-[36px] p-1.5 relative"
  >
    <div
      style={{ background: bg }}
      className="w-full h-full rounded-[30px] overflow-hidden relative"
    >
      {notch && (
        <div className="absolute top-[9px] left-1/2 -translate-x-1/2 w-[78px] h-[22px] rounded-full bg-[#1A1A1A] z-[2]" />
      )}
      {children}
    </div>
  </div>
);

/* ─── Preview compositions ───────────────────────────────────────────── */

const PreviewStep1 = () => (
  <div className="relative w-full h-full flex items-center justify-center">
    <div className="relative">
      <img
        src="https://cpnxwtiyemsrigrtetmd.supabase.co/storage/v1/object/public/assets/hero/viralisek_brak_weny.jpg"
        alt="Viralisek bez pomysłu na nagranie"
        loading="lazy"
        decoding="async"
        width={340}
        height={340}
        className="w-[340px] h-[340px] rounded-[28px] object-cover shadow-[0_20px_44px_-14px_rgba(0,0,0,0.28)]"
      />
      <div
        style={{ boxShadow: "0 8px 18px -6px rgba(0,0,0,0.18)" }}
        className="absolute -top-9 -right-28 rotate-[8deg] bg-[#FFF7C2] px-4 py-2.5 rounded text-[#5a4a2a] italic font-semibold text-2xl"
      >
        co dziś nagrać?
      </div>
      <div
        style={{ boxShadow: "0 8px 18px -6px rgba(0,0,0,0.18)" }}
        className="absolute -bottom-3 -left-32 -rotate-[6deg] bg-[#FDDDE0] px-4 py-2.5 rounded text-[#5a4a2a] italic font-semibold text-[22px]"
      >
        brak pomysłu…
      </div>
      <div
        style={{ boxShadow: "0 8px 18px -6px rgba(0,0,0,0.18)" }}
        className="absolute top-14 -left-36 rotate-[4deg] bg-[#D9ECFB] px-3.5 py-2 rounded text-[#1A4E78] italic font-semibold text-lg"
      >
        zero inspiracji
      </div>
      <div
        style={{ boxShadow: "0 8px 18px -6px rgba(0,0,0,0.18)" }}
        className="absolute bottom-12 -right-32 -rotate-[3deg] bg-white px-3.5 py-2 rounded text-gray-500 line-through italic font-semibold text-lg"
      >
        nagrać… coś?
      </div>
      <svg
        width="44"
        height="30"
        viewBox="0 0 44 30"
        className="absolute top-2 -left-12"
        aria-hidden="true"
      >
        <path
          d="M4 22 Q 18 6, 38 8"
          stroke="#1A1A1A"
          strokeWidth="2.5"
          fill="none"
          strokeLinecap="round"
        />
        <path
          d="M38 8 L 32 5 M 38 8 L 36 14"
          stroke="#1A1A1A"
          strokeWidth="2.5"
          fill="none"
          strokeLinecap="round"
        />
      </svg>
    </div>
  </div>
);

const PreviewStep2 = () => {
  const cards = [
    { t: "Dlaczego jesz sałatkę i ciągle jesteś głodna", score: "88,4", time: "2 h temu", bg: "#FFF7C2" },
    { t: "Produkt „fit\" tuczy bardziej niż chipsy", score: "95,6", time: "1 dzień temu", bg: "#FDDDE0" },
    { t: "3 rzeczy, które dietetycy jedzą w sekrecie", score: "82,7", time: "1 dzień temu", bg: "#D9ECFB" },
    { t: "5 mitów o białku, które wciąż powtarzasz", score: "70,4", time: "2 dni temu", bg: "#D8EFC9" },
  ];
  return (
    <div className="relative w-full h-full flex items-center justify-center">
      <div className="relative">
        <Phone>
          <div className="pt-9 px-3 pb-4 h-full flex flex-col">
            <div className="flex items-center gap-2 mb-2 px-1">
              <span className="w-[28px] h-[28px] rounded-lg bg-[#E8F4FD] inline-flex items-center justify-center">
                <Magnet className="w-4 h-4" color="#1A7ACC" strokeWidth={2.2} />
              </span>
              <span className="font-bold text-[15px] text-[#1A1A1A]">Biblioteka</span>
            </div>
            <div className="text-[10px] text-gray-500 mb-2.5 px-1">
              Środa, 12 maja · 6 nowych
            </div>
            {cards.map((c, i) => (
              <div
                key={i}
                style={{ background: c.bg, boxShadow: "0 2px 5px -2px rgba(0,0,0,0.06)" }}
                className="rounded-xl px-2.5 py-2 mb-1.5"
              >
                <div className="flex items-center justify-between gap-1.5 mb-1">
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-white/85 shadow-sm">
                    <TrendingUp className="w-2.5 h-2.5" color="#10b981" strokeWidth={2.5} />
                    <span className="text-[10px] font-bold tabular-nums text-emerald-600 leading-none">
                      {c.score}
                    </span>
                    <span className="text-[8px] text-gray-400 leading-none">/100</span>
                  </span>
                  <div className="flex items-center gap-0.5 text-gray-500">
                    <span className="text-[8.5px] mr-0.5 whitespace-nowrap">{c.time}</span>
                    <Heart className="w-2.5 h-2.5" strokeWidth={1.8} />
                    <CheckCircle className="w-2.5 h-2.5" strokeWidth={1.8} />
                    <MoreVertical className="w-2.5 h-2.5" strokeWidth={1.8} />
                  </div>
                </div>
                <div className="text-[11.5px] font-bold leading-tight text-[#1A1A1A] line-clamp-2">
                  {c.t}
                </div>
              </div>
            ))}
          </div>
        </Phone>
        <div
          style={{
            boxShadow: "0 18px 40px -16px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)",
          }}
          className="absolute top-14 -right-44 w-[220px] bg-white rounded-2xl px-4 py-3.5 rotate-[3deg]"
        >
          <div className="font-mono text-[10px] text-gray-500 mb-2">Twoja tematyka</div>
          <div className="flex flex-wrap gap-1.5">
            {["zdrowe odżywianie", "fitness", "mity dietetyczne"].map((t) => (
              <span
                key={t}
                className="bg-[#FFEFE2] text-[#E8630A] px-2.5 py-1 rounded-full text-[11px] font-semibold"
              >
                {t}
              </span>
            ))}
          </div>
          <div className="mt-3 text-xs text-[#1A1A1A] font-bold leading-snug">
            6 nowych pomysłów
            <br />
            <span className="text-[#2D8B4E] font-normal text-[11px]">dziś rano</span>
          </div>
        </div>
        <div
          style={{ boxShadow: "0 10px 24px -10px rgba(0,0,0,0.2)" }}
          className="absolute bottom-12 -left-40 -rotate-[4deg] bg-[#FFF7C2] px-3.5 py-3 rounded italic font-semibold text-[#5a4a2a] text-[22px] leading-tight max-w-[200px]"
        >
          świeże pomysły
          <br />
          każdego ranka ☕
        </div>
      </div>
    </div>
  );
};

const PreviewStep3 = () => (
  <div className="relative w-full h-full flex items-center justify-center">
    <div className="relative">
      <Phone>
        <div className="pt-9 px-3 pb-3 h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between mb-2 px-1">
            <div className="flex items-center gap-2">
              <span className="w-7 h-7 rounded-lg bg-[#1A1A1A] text-white inline-flex items-center justify-center text-sm">
                🎬
              </span>
              <span className="font-bold text-[15px] text-[#1A1A1A]">Scenariusz</span>
            </div>
            <span className="bg-[#E6F3EB] text-[#2D8B4E] px-2 py-[2px] rounded-full text-[9px] font-bold tracking-wider">
              GOTOWY
            </span>
          </div>
          <div className="text-[10px] text-gray-500 mb-2.5 px-1">
            Temat · „Mity dietetyczne"
          </div>

          {/* Top section list — components built BEFORE the script */}
          <div className="space-y-1 mb-2">
            {[
              { ic: "✎", t: "Hooki", s: "10 wariantów otwarcia", c: "#E8F4FD", ic_c: "#1A7ACC" },
              { ic: "Aa", t: "Nakładki tekstowe", s: "10 gotowych", c: "#F1E6F8", ic_c: "#7C4FAB" },
            ].map((r) => (
              <div key={r.t} className="flex items-center gap-2.5 px-0.5 py-0.5">
                <div
                  style={{ background: r.c, color: r.ic_c }}
                  className="w-7 h-7 rounded-lg font-extrabold text-xs flex items-center justify-center flex-[0_0_28px]"
                >
                  {r.ic}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[12.5px] font-bold text-[#1A1A1A] leading-tight">{r.t}</div>
                  <div className="text-[10.5px] text-gray-500 leading-tight">{r.s}</div>
                </div>
                <Check className="w-3.5 h-3.5" color="#2D8B4E" strokeWidth={3} />
              </div>
            ))}
          </div>

          {/* Scenariusz expanded block */}
          <div className="rounded-xl bg-[#EEF3FF] p-2 mb-2">
            <div className="flex items-center justify-between mb-1.5 px-0.5">
              <div className="flex items-center gap-1.5">
                <span className="text-[11px]">🎬</span>
                <span className="font-bold text-[10.5px] text-[#1A1A1A]">Scenariusz</span>
              </div>
              <span className="inline-flex items-center gap-0.5 bg-white text-[#3B82F6] text-[8.5px] font-semibold px-1.5 py-[2px] rounded-md border border-[#3B82F6]/20">
                <PlayCircle className="w-2.5 h-2.5" strokeWidth={2.2} />
                Prompter
              </span>
            </div>
            <div className="bg-white rounded-lg px-2 py-1.5 text-[10.5px] leading-[1.45] text-[#1A1A1A]">
              Wiesz, że sałatka, którą jesz, może mieć więcej kalorii niż pizza?
              Pokażę Ci 3 przykłady, które najbardziej Cię zaskoczą.
              Bo to wcale nie kalorie sprawiają, że tyjesz<span className="text-gray-400">…</span>
            </div>
            <div className="mt-1.5 bg-[#3B82F6] text-white text-[9.5px] font-semibold rounded-md py-1 flex items-center justify-center gap-1">
              <span className="text-[9px]">▾</span>
              Rozwiń
            </div>
          </div>

          {/* Bottom section list — verification AFTER the script */}
          <div className="space-y-1">
            {[
              { ic: "✦", t: "Ciekawostki", s: "5 sprawdzonych", c: "#FFF7C2", ic_c: "#B58A0F" },
              { ic: "✓", t: "Fact-checking", s: "3 niezależne źródła", c: "#E6F3EB", ic_c: "#2D8B4E" },
            ].map((r) => (
              <div key={r.t} className="flex items-center gap-2.5 px-0.5 py-0.5">
                <div
                  style={{ background: r.c, color: r.ic_c }}
                  className="w-7 h-7 rounded-lg font-extrabold text-xs flex items-center justify-center flex-[0_0_28px]"
                >
                  {r.ic}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[12.5px] font-bold text-[#1A1A1A] leading-tight">{r.t}</div>
                  <div className="text-[10.5px] text-gray-500 leading-tight">{r.s}</div>
                </div>
                <Check className="w-3.5 h-3.5" color="#2D8B4E" strokeWidth={3} />
              </div>
            ))}
          </div>
        </div>
      </Phone>
      <div
        style={{
          boxShadow: "0 18px 40px -16px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)",
        }}
        className="absolute top-10 -left-52 w-[230px] bg-white rounded-2xl px-4 py-3.5 -rotate-[3deg]"
      >
        <div className="flex items-center gap-[7px] mb-2.5">
          <span className="w-[22px] h-[22px] rounded-md bg-[#E8F4FD] text-[#1A7ACC] text-xs font-bold inline-flex items-center justify-center">
            ✎
          </span>
          <span className="font-bold text-xs">Otwarcie filmu</span>
        </div>
        <div className="bg-[#F7F4EF] px-[11px] py-2.5 rounded-lg text-[12.5px] leading-snug text-[#1A1A1A]">
          Wiesz, że sałatka, którą jesz, ma więcej kalorii niż pizza?
        </div>
        <div className="mt-2 flex items-center justify-between text-[10px] text-gray-500 font-mono">
          <span>‹</span>
          <span>3 / 10</span>
          <span>›</span>
        </div>
      </div>
      <div
        style={{
          boxShadow: "0 18px 40px -16px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)",
        }}
        className="absolute bottom-12 -right-44 w-[200px] bg-white rounded-2xl px-3.5 py-3 rotate-[3deg]"
      >
        <div className="flex items-center gap-2 mb-1.5">
          <span className="w-[22px] h-[22px] rounded-full bg-[#E6F3EB] text-[#2D8B4E] flex items-center justify-center font-extrabold">
            ✓
          </span>
          <span className="text-[11px] font-bold">Fact-checking</span>
        </div>
        <div className="text-[11px] text-[#1A1A1A] leading-snug">
          Wszystkie 5 ciekawostek zweryfikowane na podstawie 3 niezależnych źródeł.
        </div>
      </div>
    </div>
  </div>
);

const PreviewStep4 = () => (
  <div className="relative w-full h-full flex items-center justify-center">
    <div className="relative">
      <Phone bg="#0E0E12">
        <div className="absolute inset-0 flex flex-col text-white">
          {/* Top bar */}
          <div className="flex items-center justify-between px-3.5 pt-[34px] pb-1.5 text-[10px] text-white/85">
            <div className="flex items-center gap-1.5">
              <span className="text-sm leading-none">×</span>
              <span>Zamknij prompter</span>
            </div>
            <span className="font-mono text-[11px]">⛶</span>
          </div>

          {/* Scenario subtitle */}
          <div className="px-4 mt-3 mb-2 text-[9.5px] text-white/40 leading-snug">
            Mity dietetyczne · scenariusz
          </div>

          {/* Big teleprompter text */}
          <div className="flex-1 flex items-center px-4">
            <p className="text-[15px] font-semibold leading-[1.45] tracking-tight text-white">
              Wiesz, że sałatka, którą jesz, może mieć więcej kalorii niż pizza?{" "}
              <span className="text-[#FF8C00]">Pokażę Ci 3 przykłady.</span>
            </p>
          </div>

          {/* Speed slider */}
          <div className="px-4">
            <div className="flex items-center gap-2">
              <span className="text-[9px] text-white/55">Wolno</span>
              <div className="flex-1 relative h-1 rounded-full">
                <div
                  className="absolute inset-0 rounded-full"
                  style={{ background: "linear-gradient(to right, #3B82F6, #7C3AED)" }}
                />
                <div className="absolute left-[58%] top-1/2 -translate-y-1/2 -translate-x-1/2 w-2.5 h-2.5 rounded-full bg-white shadow" />
              </div>
              <span className="text-[9px] text-white/55">Szybko</span>
            </div>
          </div>

          {/* Bottom toolbar */}
          <div className="px-3 py-3 mt-2 flex items-center justify-center gap-2 text-[9.5px] text-white/70">
            <span>M</span>
            <span>L</span>
            <span className="bg-white text-black px-1.5 py-0.5 rounded text-[9px] font-bold">XL</span>
            <span className="ml-0.5 text-[12px] leading-none">↺</span>
            <span className="w-[26px] h-[26px] rounded-full bg-[#3B82F6] inline-flex items-center justify-center text-white">
              <Play className="w-2.5 h-2.5 fill-current" strokeWidth={0} />
            </span>
            <span className="ml-0.5 text-[12px] leading-none tracking-tighter">⫶⫶</span>
            <span>6</span>
          </div>
        </div>
      </Phone>
      <div
        style={{
          boxShadow: "0 18px 40px -16px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)",
        }}
        className="absolute top-20 -left-52 w-[220px] bg-white rounded-2xl px-4 py-3.5 -rotate-[3deg]"
      >
        <div className="font-mono text-[10px] text-gray-500 mb-2.5">Prędkość przewijania</div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-gray-500">żółw</span>
          <div className="flex-1 h-1.5 bg-[#F7F4EF] rounded-full relative">
            <div
              className="absolute left-0 top-0 h-full rounded-full"
              style={{ width: "58%", background: "linear-gradient(to right, #3B82F6, #7C3AED)" }}
            />
            <div
              className="absolute top-[-4px] w-3.5 h-3.5 rounded-full bg-white border-2 border-[#7C3AED]"
              style={{ left: "58%", transform: "translateX(-7px)" }}
            />
          </div>
          <span className="text-[11px] text-gray-500">gepard</span>
        </div>
        <div className="mt-2.5 text-xs text-[#1A1A1A]">
          <strong>1,2×</strong>{" "}
          <span className="text-gray-500">· dopasowane do Ciebie</span>
        </div>
      </div>
      <div
        style={{ boxShadow: "0 18px 40px -16px rgba(0,0,0,0.35)" }}
        className="absolute bottom-20 -right-40 w-[190px] bg-[#1A1A1A] text-white rounded-2xl px-4 py-3.5 rotate-[3deg]"
      >
        <div className="flex items-center gap-2">
          <PlayCircle className="w-3 h-3 text-[#FF8C00] fill-[#FF8C00]/20" strokeWidth={2.2} />
          <span className="text-[11px] font-bold font-mono tracking-wider">TELEPROMPTER</span>
        </div>
        <div className="mt-2 text-[13px] text-white leading-snug">
          Tekst sam się przewija.
          <br />
          <span className="text-[#FF8C00]">Czytasz prosto z ekranu.</span>
        </div>
      </div>
    </div>
  </div>
);

const PreviewStep5 = () => (
  <div className="relative w-full h-full flex items-center justify-center">
    <div className="relative">
      <Phone>
        <div className="absolute inset-0 flex flex-col">
          <div className="flex-1 relative overflow-hidden">
            <img
              src="https://cpnxwtiyemsrigrtetmd.supabase.co/storage/v1/object/public/assets/hero/viralisek_nagrywa_dieta.jpg"
              alt="Podgląd rolki — Viralisek nagrywa o diecie"
              loading="lazy"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover"
            />
            {/* Soft gradient overlays for legibility */}
            <div
              className="absolute inset-x-0 top-0 h-24 pointer-events-none"
              style={{ background: "linear-gradient(to bottom, rgba(0,0,0,0.45), transparent)" }}
            />
            <div
              className="absolute inset-x-0 bottom-0 h-28 pointer-events-none"
              style={{ background: "linear-gradient(to top, rgba(0,0,0,0.6), transparent)" }}
            />
            <div
              className="absolute top-1/2 left-1/2 w-[52px] h-[52px] rounded-full flex items-center justify-center text-xl"
              style={{
                transform: "translate(-50%,-50%)",
                background: "rgba(255,255,255,0.88)",
              }}
            >
              ▶
            </div>
            <div
              className="absolute top-[18px] left-4 text-white px-2.5 py-1 rounded-full text-[10px] font-mono"
              style={{ background: "rgba(0,0,0,0.6)" }}
            >
              1,2M wyświetleń
            </div>
            <div className="absolute bottom-3.5 left-4 right-4 text-white text-[11px] leading-snug">
              <div className="font-bold">@twoje_konto</div>
              <div>Czy „fit" naprawdę znaczy zdrowo? Sprawdzamy 3 produkty…</div>
            </div>
          </div>
          <div className="px-3.5 py-3 flex items-center justify-around text-xs font-semibold">
            <span className="flex items-center gap-1.5">
              ♥ <span className="text-gray-500">124K</span>
            </span>
            <span className="flex items-center gap-1.5">
              💬 <span className="text-gray-500">2,1K</span>
            </span>
            <span className="flex items-center gap-1.5">
              ↗ <span className="text-gray-500">8 432</span>
            </span>
            <span className="flex items-center gap-1.5">
              ☆ <span className="text-gray-500">3,4K</span>
            </span>
          </div>
        </div>
      </Phone>
      <div
        style={{
          boxShadow: "0 18px 40px -16px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)",
        }}
        className="absolute top-12 -right-52 w-[250px] bg-white rounded-2xl px-4 py-3.5 rotate-[3deg]"
      >
        <div className="font-mono text-[10px] text-gray-500 mb-1">
          Twoje konto · ostatnie 7 dni
        </div>
        <div className="text-2xl font-extrabold text-[#2D8B4E]">+2 856</div>
        <div className="text-[11px] text-gray-500 mb-2.5">nowych obserwujących</div>
        <svg width="218" height="48" viewBox="0 0 218 48" aria-hidden="true">
          <defs>
            <linearGradient id="hiw-grad-g" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#2D8B4E" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#2D8B4E" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path
            d="M2 40 L 30 36 L 58 32 L 86 26 L 114 24 L 142 16 L 170 14 L 218 4 L 218 48 L 2 48 Z"
            fill="url(#hiw-grad-g)"
          />
          <path
            d="M2 40 L 30 36 L 58 32 L 86 26 L 114 24 L 142 16 L 170 14 L 218 4"
            stroke="#2D8B4E"
            strokeWidth="2.5"
            fill="none"
            strokeLinecap="round"
          />
          <circle cx="218" cy="4" r="4" fill="#2D8B4E" />
        </svg>
      </div>
      <div
        style={{
          boxShadow: "0 18px 40px -16px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)",
        }}
        className="absolute bottom-12 -left-44 w-[210px] bg-white rounded-2xl px-4 py-3 -rotate-[3deg]"
      >
        <div className="flex items-center gap-3">
          <div className="relative w-[46px] h-[46px] flex items-center justify-center">
            <svg className="w-[46px] h-[46px] -rotate-90" viewBox="0 0 40 40" aria-hidden="true">
              <circle cx="20" cy="20" r="18" fill="none" stroke="#E6F3EB" strokeWidth="3" />
              <circle
                cx="20"
                cy="20"
                r="18"
                fill="none"
                stroke="#10b981"
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray={2 * Math.PI * 18}
                strokeDashoffset={2 * Math.PI * 18 * (1 - 0.934)}
              />
            </svg>
            <span className="absolute text-[11px] font-bold leading-none text-emerald-600">93</span>
          </div>
          <div>
            <div className="text-[10px] font-semibold text-gray-500 tracking-widest">
              VIRAL SCORE
            </div>
            <div className="flex items-baseline gap-0.5 mt-0.5">
              <span className="text-[18px] font-bold tabular-nums text-emerald-600 leading-none">
                93,4
              </span>
              <span className="text-[11px] text-gray-400 leading-none">/100</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const PREVIEWS: Record<number, () => JSX.Element> = {
  1: PreviewStep1,
  2: PreviewStep2,
  3: PreviewStep3,
  4: PreviewStep4,
  5: PreviewStep5,
};

/* ─── Tile ─────────────────────────────────────────────────────────────── */

const Tile = ({
  step,
  active,
  onClick,
  compact = false,
  interactive = true,
}: {
  step: Step;
  active: boolean;
  onClick?: () => void;
  compact?: boolean;
  interactive?: boolean;
}) => {
  const Icon = step.Icon;
  return (
    <button
      type="button"
      onClick={interactive ? onClick : undefined}
      aria-hidden={!interactive}
      tabIndex={interactive ? 0 : -1}
      style={{
        background: active ? step.bg : "#F1EEE8",
        borderColor: active ? step.iconStroke : "transparent",
      }}
      className={cn(
        "w-full text-left rounded-2xl border-2 transition-colors duration-200 flex flex-col gap-3 text-[#1A1A1A] outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-orange-500",
        compact ? "p-4" : "p-5",
        interactive ? "cursor-pointer" : "cursor-default",
      )}
    >
      <div className="flex items-center gap-3">
        <span
          style={{
            background: active ? "#fff" : step.iconTint,
            boxShadow: active ? "0 4px 10px -4px rgba(0,0,0,0.12)" : undefined,
          }}
          className="w-[38px] h-[38px] rounded-xl inline-flex items-center justify-center flex-[0_0_38px]"
        >
          <Icon className="w-[22px] h-[22px]" color={step.iconStroke} strokeWidth={2} />
        </span>
        <span
          style={{ color: active ? step.iconStroke : "#9a9a9a" }}
          className="font-mono text-[11px] font-semibold tracking-wider"
        >
          KROK 0{step.id}
        </span>
        {interactive && (
          <span
            style={{
              background: active ? step.iconStroke : "transparent",
              color: active ? "#fff" : "#9a9a9a",
              borderColor: active ? "transparent" : "#d6d2c8",
              transform: active ? "rotate(45deg)" : "rotate(0deg)",
            }}
            className="ml-auto w-7 h-7 rounded-full flex items-center justify-center border-[1.5px] transition-all duration-200 font-semibold"
          >
            <Plus className="w-3.5 h-3.5" />
          </span>
        )}
      </div>
      <div className={cn("leading-snug", compact ? "text-[15px]" : "text-base")}>
        <strong
          className={cn(
            "font-bold tracking-tight",
            compact ? "text-[17px]" : "text-[19px]",
          )}
        >
          {step.titleBold}
        </strong>
        <span className="text-[#3a3a3a]"> — {step.rest}</span>
      </div>
    </button>
  );
};

/* ─── Section ──────────────────────────────────────────────────────────── */

const HowItWorksSection = () => {
  const [active, setActive] = useState(1);
  const cur = STEPS.find((s) => s.id === active) ?? STEPS[1];
  const Preview = PREVIEWS[active];

  return (
    <section
      id="trzy-kroki"
      className="relative py-20 md:py-28 overflow-hidden scroll-mt-24 bg-[#F7F4EF] text-[#1A1A1A]"
    >
      {/* Top bridge — fades in from Hero's dark video ending directly above */}
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-56 md:h-72 pointer-events-none"
        style={{
          background:
            "linear-gradient(to bottom, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.7) 25%, rgba(0, 0, 0, 0.25) 65%, transparent 100%)",
        }}
      />
      {/* Bottom bridge — fades out to blue FeaturesGrid below */}
      <div
        aria-hidden="true"
        className="absolute inset-x-0 bottom-0 h-32 md:h-40 pointer-events-none"
        style={{ background: "linear-gradient(to top, #0080FF, transparent)" }}
      />

      <div className="relative z-10 max-w-[1480px] mx-auto px-4 sm:px-8 lg:px-16">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="text-center max-w-[880px] mx-auto mb-14 md:mb-16"
        >
          <h2 className="font-extrabold leading-[1.02] tracking-tight text-4xl md:text-6xl lg:text-[64px]">
            Od pomysłu do{" "}
            <span className="relative inline-block text-[#E8630A]">
              viralowej rolki.
              <span className="absolute left-0 right-0 -bottom-2">
                <Squiggle w={360} color="#E8630A" stroke={5} />
              </span>
            </span>
          </h2>
          <p className="mt-6 max-w-[620px] mx-auto text-base md:text-lg leading-relaxed text-[#3a3a3a]">
            Pięć kroków, które zamieniają „nie wiem co nagrać" w{" "}
            <strong>opublikowany film</strong>. Kliknij krok, żeby zobaczyć szczegóły.
          </p>
        </motion.div>

        {/* Mobile: all steps expanded by default — no tap-to-reveal, every
            step's preview is visible on load so nothing is hidden behind a
            "+" tap. */}
        <div className="md:hidden flex flex-col gap-2.5">
          {STEPS.map((s) => {
            const PreviewInline = PREVIEWS[s.id];
            return (
              <div key={s.id}>
                <Tile step={s} active interactive={false} compact />
                <div
                  style={{
                    background: s.bg,
                    boxShadow: "inset 0 0 0 1px rgba(26,26,26,0.04)",
                  }}
                  className="mt-2.5 rounded-2xl h-[420px] relative overflow-hidden"
                >
                  <div className="absolute inset-0 scale-[0.7] origin-center">
                    <PreviewInline />
                  </div>
                  <div className="absolute top-3.5 left-4 flex items-center gap-2.5">
                    <span
                      style={{ color: s.iconStroke }}
                      className="w-7 h-7 rounded-md bg-white inline-flex items-center justify-center font-extrabold text-xs"
                    >
                      {s.id}
                    </span>
                    <span className="font-mono text-[10px] font-bold tracking-widest">
                      KROK 0{s.id}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Desktop: tile column + preview panel */}
        <div className="hidden md:grid grid-cols-[440px_1fr] gap-8 items-start">
          <div className="flex flex-col gap-3">
            {STEPS.map((s) => (
              <Tile
                key={s.id}
                step={s}
                active={s.id === active}
                onClick={() => setActive(s.id)}
              />
            ))}
          </div>
          <div
            style={{
              background: cur.bg,
              boxShadow: "inset 0 0 0 1px rgba(26,26,26,0.04)",
            }}
            className="rounded-[28px] h-[720px] relative overflow-hidden"
          >
            <Preview />
            <div className="absolute top-5 left-6 flex items-center gap-2.5">
              <span
                style={{ color: cur.iconStroke }}
                className="w-[30px] h-[30px] rounded-lg bg-white inline-flex items-center justify-center font-extrabold text-sm"
              >
                {cur.id}
              </span>
              <span className="font-mono text-[11px] text-[#1A1A1A] font-bold tracking-widest">
                KROK 0{cur.id}
              </span>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 md:mt-14 flex justify-center">
          <a
            href="/auth?mode=register&trial=true"
            className="relative overflow-hidden group inline-flex items-center gap-2 px-8 py-4 bg-orange-500 text-white font-bold text-lg rounded-full shadow-xl shadow-orange-500/25 hover:bg-orange-600 hover:shadow-2xl active:scale-[0.98] transition-all duration-300"
          >
            <span
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "linear-gradient(120deg, transparent 35%, rgba(255,255,255,0.15) 50%, transparent 65%)",
                backgroundSize: "200% 100%",
                animation: "shimmer-sweep 4s ease-in-out infinite",
              }}
            />
            <span className="relative z-10">Zacznij za darmo</span>
            <ArrowRight className="relative z-10 w-5 h-5 transition-transform group-hover:translate-x-1" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
